import {
    SubscriptionResponse,
    TopUpCatalogResponse,
    TopUpCheckoutItem,
    TopUpPackResponse
} from '@app/Subscription/types/subscription';

// Where the top-up modal was opened from — drives which packs are pre-selected
// (the "bundle as default" UX: the typical pair is pre-picked, either item removable).
export type TopUpContext = 'plan' | 'ai' | 'campaign';

export interface TopUpSelection {
    sku: string;
    selected: boolean;
    quantity: number; // pack units, >= 1
}

// AI packs are pointless on unlimited-AI tiers; everyone else — including users
// with no subscription at all — can buy them.
export function visibleTopUpPacks(
    packs: TopUpPackResponse[],
    sub: SubscriptionResponse | null
): TopUpPackResponse[] {
    const hideAi = sub != null && sub.aiAds.limit === null;
    return packs.filter((p) => p.creditType !== 'AI_AD' || !hideAi);
}

export function defaultTopUpSelection(
    context: TopUpContext,
    catalog: TopUpCatalogResponse,
    sub: SubscriptionResponse | null
): TopUpSelection[] {
    const packs = visibleTopUpPacks(catalog.packs, sub);
    const bundleQty = new Map(catalog.defaultBundle.map((b) => [b.sku, b.quantity]));
    return packs.map((pack) => {
        const selected =
            context === 'plan' ||
            (context === 'ai' && pack.creditType === 'AI_AD') ||
            (context === 'campaign' && pack.creditType === 'CAMPAIGN');
        return { sku: pack.sku, selected, quantity: Math.max(1, bundleQty.get(pack.sku) ?? 1) };
    });
}

export function computeTopUpTotal(selection: TopUpSelection[], packs: TopUpPackResponse[]): number {
    return selection.reduce((sum, item) => {
        if (!item.selected) return sum;
        const pack = packs.find((p) => p.sku === item.sku);
        return pack ? sum + pack.priceCents * item.quantity : sum;
    }, 0);
}

export function selectedTopUpItems(selection: TopUpSelection[]): TopUpCheckoutItem[] {
    return selection
        .filter((item) => item.selected)
        .map((item) => ({ sku: item.sku, quantity: item.quantity }));
}
